import React, { useState } from 'react'
import LabeledValue from '../../../../components/common/LabeledValue'
import Table from '../../../../components/common/Table/Table'
import Modal from '../../../../components/common/Modal/Modal'
import Loader from '../../../../components/common/Loader'
import { RetractionSummary, PartialRetractionSummary } from '../RetractionSummaries'
import { TimeOffRequestDetails, isVacationBid } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import { RETRACT_TABLE_COLUMNS, normalizeStatus, type TableRow } from './constants'
import { getVacationBidAwareTableProps } from './vacationBidOverlapDisplay'
import RetractForm from './RetractForm'
import RightPanel from './RightPanel'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import WithdrawnView from './WithdrawnView'
import { useRetractRequest } from '../../../../hooks/useRetractRequest'
import { Link, useNavigate } from 'react-router-dom'


interface ApprovedViewProps {
  data: TimeOffRequestDetails
  tableRows: TableRow[]
  highlightedDates: string[]
  handlePrint?: () => void
}

const PARTIAL_RETRACTION_SUBMITTED_HEADING = 'Request for Partial Retraction Submitted'
const APPROVED_HEADING = 'Approved Time Off Request'

/**
 * Derives the page heading from the current view state.
 * Extracted as a pure function to avoid `let`-mutation inside the component body.
 */
function resolveHeading(
  isRTSubmitted: boolean,
  mgrRequested: boolean,
  retractSubmitted: boolean,
  isRetractOpen: boolean,
  isPartialRetract: boolean,
  isPRTSubmitted: boolean,
  isApprovedPRTSubmitted: boolean
): string {
  if (isApprovedPRTSubmitted) return APPROVED_HEADING
  if (isPRTSubmitted) return PARTIAL_RETRACTION_SUBMITTED_HEADING
  if (isRTSubmitted && mgrRequested) return 'Request for Time Off Retraction Submitted'
  if (isRTSubmitted) return 'Time Off Request Retraction Submitted'
  if (retractSubmitted && isPartialRetract) return PARTIAL_RETRACTION_SUBMITTED_HEADING
  if (retractSubmitted) return 'Request for Time Off Retraction Submitted'
  if (isRetractOpen && isPartialRetract) return 'Partially Retract an Approved Time Off Request'
  if (isRetractOpen) return 'Retract an Approved Time Off Request'
  return APPROVED_HEADING
}

const ApprovedView: React.FC<ApprovedViewProps> = ({ data, tableRows, highlightedDates, handlePrint }) => {
  const navigate = useNavigate()
  const isAdminMode = globalThis.location.pathname.startsWith('/admin')
  const normalizedStatus = normalizeStatus(data.status)
  const isRTSubmitted =
    normalizedStatus === 'rtsubmitted' ||
    normalizedStatus === 'rtsubmitted_released'
  const isPRTSubmitted =
    normalizedStatus === 'prtsubmitted' ||
    normalizedStatus === 'prtsubmitted_released'
  const isRtSubmittedReleased = normalizedStatus === 'rtsubmitted_released'
  const isPrtSubmittedReleased = normalizedStatus === 'prtsubmitted_released'
  const isRetractionReleased = isRtSubmittedReleased || isPrtSubmittedReleased
  const isApprovedPRTSubmitted = normalizedStatus === 'approved_prtsubmitted'
  const mgrRequested = data.mgrRequestedRetraction === 'Y'
  const [isRetractOpen, setIsRetractOpen] = useState(false)
  const [isPartialRetract, setIsPartialRetract] = useState(false)
  const {
    partialRetract,
    setPartialRetract,
    comments,
    setComments,
    retractError,
    isSubmittingRetract,
    retractSubmitted,
    submitRetractRequest,
    submitPartialRetractRequest,
    resetRetractForm,
  } = useRetractRequest({ requestId: data.requestId, fallbackNuid: data.nuid })
  const [summaryType, setSummaryType] = useState<'none' | 'retract' | 'partial'>('none')
  const [retractDays, setRetractDays] = useState<Record<string, boolean>>({})
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [withdrawn, setWithdrawn] = useState(false)

  const handleRetractRequest = async () => {
    const wasSubmitted = await submitRetractRequest()
    if (wasSubmitted) {
      setSummaryType('retract')
    }
  }

  const heading = resolveHeading(isRTSubmitted, mgrRequested, retractSubmitted, isRetractOpen, isPartialRetract, isPRTSubmitted, isApprovedPRTSubmitted)

  const dataRowsOnly = tableRows.filter((r) => r.id !== 'total')
  const selectedPartialRetractIds = dataRowsOnly
    .filter((row) => !!retractDays[row.id])
    .map((row) => Number(row.toReqDtlId))
    .filter((id) => Number.isFinite(id))
  const hasAtLeastOneSelected = selectedPartialRetractIds.length > 0
  const hasAtLeastOneRemaining = dataRowsOnly.length - selectedPartialRetractIds.length >= 1
  const canSubmitPartialRetract = hasAtLeastOneSelected && hasAtLeastOneRemaining

  const handlePartialRetractRequest = async () => {
    const wasSubmitted = await submitPartialRetractRequest(selectedPartialRetractIds)
    if (wasSubmitted) {
      navigate(isAdminMode ? '/admin/requestor' : '/requestor')
    }
  }

  const handleCancelRetract = () => {
    setIsRetractOpen(false)
    resetRetractForm()
  }

  const handleCancelPartialRetract = () => {
    setIsRetractOpen(false)
    setIsPartialRetract(false)
    resetRetractForm()
    setRetractDays({})
  }

const getSubmittedBy = () => {
  const latestStatus = data.statuses?.[0]
  if (latestStatus?.updatedBy === data.nuid) {
    return `${latestStatus?.fullName} (${latestStatus?.updatedBy})`
  }

  // Reaching here means updatedBy (when present) already differs from the request
  // owner's nuid — not gated on manualReqRcvdDate, since a proxy can submit a
  // retraction/resubmission the employee originally submitted themselves.
  if (latestStatus?.updatedBy) {
    return `Requestor Proxy ${latestStatus.fullName} (${latestStatus.updatedBy})`
  }
  return data.submittedBy || 'N/A'
}

  const renderRetractedView = () => {
    const retractLabel = isPRTSubmitted ? 'Partial Retraction Submitted On' : 'Retraction Submitted On'
    return (
      <>
        {data.retractionSubmittedOn && (
          <div className="mb-4"><LabeledValue label={retractLabel} value={data.retractionSubmittedOn} /></div>
        )}
        {data.submittedBy && (
          <div className="mb-4"><LabeledValue label="Submitted By" value={getSubmittedBy()} /></div>
        )}
        {isPRTSubmitted && data.originalRequestId && (
          <div className="mb-4">
            <LabeledValue
              label="Original Time Off Request ID"
              value={
                <Link
                  to={isAdminMode?`/admin/requestor/status/${data.originalRequestId}`:`/requestor/status/${data.originalRequestId}`}
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  {data.originalRequestId}
                </Link>
              }
            />
          </div>
        )}
        {(isRTSubmitted || isPRTSubmitted) && (data.requestRank != null || isVacationBid(data)) && (
          <div className="mb-4"><LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} /></div>
        )}
        {mgrRequested && (
          <div className="mb-4">
            <p className="p3 -medium">Approver asked for this request to be retracted.</p>
          </div>
        )}
        <div className="mb-4">
          <LabeledValue
            label="Employee Comments Regarding Retraction"
            value={data.employeeRetractionComment || '-'}
          />
        </div>
      </>
    )
  }

  const renderReleasedRetractionView = () => (
    <>
      {data.holdPlacedOn && (
        <div className="mb-4">
          <LabeledValue label="Hold Released On" value={data.holdPlacedOn} />
        </div>
      )}
      {data.holdPlacedBy && (
        <div className="mb-4">
          <LabeledValue label="Released By" value={data.holdPlacedBy} />
        </div>
      )}
      {isPrtSubmittedReleased && data.originalRequestId && (
        <div className="mb-4">
          <LabeledValue
            label="Original Time Off Request ID"
            value={
              <Link
                to={isAdminMode ? `/admin/requestor/status/${data.originalRequestId}` : `/requestor/status/${data.originalRequestId}`}
                className="text-blue-600 underline hover:text-blue-800"
              >
                {data.originalRequestId}
              </Link>
            }
          />
        </div>
      )}
      {(isRtSubmittedReleased || isPrtSubmittedReleased) && (data.requestRank != null || isVacationBid(data)) && (
        <div className="mb-4">
          <LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} />
        </div>
      )}
      <div className="mb-4">
        <LabeledValue label="Approver Comments" value={data.approverComments || '-'} />
      </div>
    </>
  )

  const renderPartialRetractionSummary = () => (
    <PartialRetractionSummary
      submittedOn={data.submittedOn ?? ''}
      submittedBy={data.submittedBy ?? 'Employee'}
      requestId={data.requestId}
      comment={comments}
    />
  )

  const renderRetractionSummary = () => (
    <RetractionSummary
      approvedOn={data.approvedOn ?? ''}
      approver={data.authorizedApprover ?? ''}
      approverComments={data.approverComments ?? ''}
    />
  )

  const renderPartialRetractForm = () => (
    <>
      <div className="mb-4">
        <p className="p3 -medium">
          You are requesting to partially retract days from this Approved Time Off Request. Indicate the days
          you would like to retract by checking the retract box in the table below.<br /><br />
          To send this to your Authorized Approver, click the &apos;Yes, Partially Retract Request&apos; button
          after specifying the days to be retracted from this request.
        </p>
      </div>
      <div className="mb-4 overflow-x-auto">
        <Table
          columns={RETRACT_TABLE_COLUMNS}
          rows={dataRowsOnly.map((row) => ({
            ...row,
            retract: (
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  className="-ml-2 cursor-pointer"
                  checked={!!retractDays[row.id]}
                  onChange={(e) => setRetractDays({ ...retractDays, [row.id]: e.target.checked })}
                />
              </div>
            ),
          }))}
          caption=""
        />
      </div>
      <div className="my-10! flex items-center">
        <input
          id="partial-retract-cb"
          type="checkbox"
          checked={partialRetract}
          onChange={(e) => setPartialRetract(e.target.checked)}
          className="mr-2 cursor-pointer"
        />
        <label htmlFor="partial-retract-cb" className="p3 -book cursor-pointer">
          My Approver asked that I partially retract this Time Off Request
        </label>
      </div>
      <div className="mb-4 mt-6!">
        <label htmlFor="retract-comments" className="block p3 -medium mb-1">Comments</label>
        <textarea
          id="retract-comments"
          className="w-full border rounded p-2 min-h-20"
          maxLength={1000}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
        <div className="text-left text-xs text-gray-500 mt-1">{1000 - comments.length} of 1000 characters left</div>
      </div>
      <p className="p3 -medium">
        A partial retraction requires at least one day remaining in the request.<br />
        If you wish to retract all days, Cancel this action and click the Retract Request button.
      </p>
      <div className="flex flex-col gap-3 mt-8! desktop:flex-row desktop:items-center">
        <button type="button"
          className="button -primary w-full desktop:w-auto"
          disabled={!canSubmitPartialRetract || isSubmittingRetract}
          onClick={handlePartialRetractRequest}
        >
          {isSubmittingRetract ? 'Submitting...' : 'Yes, Partially Retract Request'}
        </button>
        <button type="button"
          className="button w-full desktop:w-auto"
          onClick={handleCancelPartialRetract}
          disabled={isSubmittingRetract}
        >
          Cancel
        </button>
      </div>
    </>
  )

  const renderRetractOpenView = () => (
    <>
      {renderRetractionSummary()}
      {!retractSubmitted && (
        <>
          {isPartialRetract ? renderPartialRetractForm() : (
            <RetractForm
              partialRetract={partialRetract}
              onPartialRetractChange={setPartialRetract}
              comments={comments}
              onCommentsChange={setComments}
              retractError={retractError}
              isSubmitting={isSubmittingRetract}
              onSubmit={handleRetractRequest}
              onCancel={handleCancelRetract}
            />
          )}
        </>
      )}
    </>
  )

  const renderApprovedPRTView = () => (
    <>
      {data.submittedBy && (
        <div className="mb-4"><LabeledValue label="Submitted By" value={getSubmittedBy()} /></div>
      )}
      {data.partialRefId && (
        <div className="mb-4">
          <LabeledValue
            label="Partial Retraction Reference ID"
            value={
              <Link
                to={isAdminMode ? `/admin/requestor/status/${data.partialRefId}` : `/requestor/status/${data.partialRefId}`}
                className="text-blue-600 underline hover:text-blue-800"
              >
                {data.partialRefId}
              </Link>
            }
          />
        </div>
      )}
      {(data.requestRank != null || isVacationBid(data)) && (
        <div className="mb-4"><LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} /></div>
      )}
      {mgrRequested && (
        <div className="mb-4">
          <p className="p3 -medium">Approver asked for this request to be retracted.</p>
        </div>
      )}
      <div className="mb-4">
        <LabeledValue
          label="Employee Comments Regarding Retraction"
          value={data.employeeRetractionComment || '-'}
        />
      </div>
    </>
  )

  const renderApprovedView = () => (
    <>
      {data.authorizedApprover && (
        <div className="mb-4">
          <LabeledValue
            label="Authorized Approver"
            value={data.authorizedApprover}
          />
        </div>
      )}
      {(data.requestRank != null || isVacationBid(data)) && (
        <div className="mb-4"><LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} /></div>
      )}
      <div className="mb-4"><LabeledValue label="Approver Comments" value={data.approverComments || '-'} /></div>
    </>
  )

  const canShowPartialRetractButton = () =>
    dataRowsOnly.length > 1 && 
    data.isPartialRetractAllowed === true && 
    data.isSingleDay !== 'Y' && 
    !data.history?.some(h => h.statusCode === 'PDENIED')

  const renderDefaultApprovedView = () => (
    <>
      {data.approvedOn && <div className="mb-4"><LabeledValue label="Approved On" value={data.approvedOn} /></div>}
      {isApprovedPRTSubmitted ? renderApprovedPRTView() : renderApprovedView()}
      {!isApprovedPRTSubmitted && (
        <div className="flex flex-col gap-3 mt-8! desktop:flex-row desktop:items-center">
          <button type="button"
            className="button -primary -inverted w-full desktop:w-auto"
            onClick={() => { setIsRetractOpen(true); setPartialRetract(false) }}
          >
            Retract Request
          </button>
          {canShowPartialRetractButton() && (
            <button type="button"
              className="button -primary -inverted w-full desktop:w-auto"
              onClick={() => {
                setIsRetractOpen(true)
                setPartialRetract(false)
                setIsPartialRetract(true)
              }}
            >
              Partially Retract Request
            </button>
          )}
        </div>
      )}
    </>
  )

  const leftContent = (() => {
    if (isRetractionReleased) return renderReleasedRetractionView()

    // RTSUBMITTED / PRTSUBMITTED: read-only view, no action buttons
    if (isRTSubmitted || isPRTSubmitted) return renderRetractedView()

    if (summaryType === 'partial') return renderPartialRetractionSummary()

    if (summaryType === 'retract') return renderRetractionSummary()

    if (isRetractOpen) return renderRetractOpenView()

    // Default approved view
    return renderDefaultApprovedView()
  })()

  return (
    <div>
      {isSubmittingRetract && <Loader />}
      {withdrawn && (
        <WithdrawnView
          data={{ ...data, status: 'Withdrawn' }}
          tableRows={tableRows}
          highlightedDates={highlightedDates}
          handlePrint={handlePrint}
          showSuccessBanner
        />
      )}
      {!withdrawn && (
        <>
          <h1 className="h1">{heading}</h1>
          <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row items-start">
            <div className="flex-1">{leftContent}</div>
            <RightPanel data={data} handlePrint={handlePrint} />
          </div>
          <div className="mt-6!"><hr className="-variation-1" /></div>
          {data.history && data.history.length > 0 && <HistorySection history={data.history} />}
          <CurrentRequestSection {...getVacationBidAwareTableProps(tableRows, isVacationBid(data))} highlightedDates={highlightedDates} />
        </>
      )}
      <Modal
        isOpen={isWithdrawOpen}
        title="Withdraw Request"
        onClose={() => setIsWithdrawOpen(false)}
        submitBtn="Withdraw Request"
        backBtn="Cancel"
        onPrimary={() => { setIsWithdrawOpen(false); setWithdrawn(true) }}
        onBack={() => setIsWithdrawOpen(false)}
      >
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <img className="w-10 h-10 shrink-0" src="/assets/images/alertSolid.svg" alt="warning" />
          <div>
            <p className="p2 -semibold">
              You are requesting to withdraw this time off request. It will be removed from the approvers queue.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ApprovedView
